import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PageMeta from "../components/PageMeta";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    clinic_name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup({ ...form, role: "admin" });
      toast.success("Welcome to HeDesk!");
      navigate("/dashboard");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-sky-50 px-4 py-10">
      <PageMeta
        title="Sign up for HeDesk — Free Clinic Management Software"
        description="Create your free HeDesk account in 60 seconds. Manage appointments, patients, prescriptions and billing in one place. Made for clinics in India."
      />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8" data-testid="signup-logo">
          <img
            src="/logo.jpeg"
            alt="HeDesk"
            className="w-10 h-10 rounded-[22%] object-cover"
          />
          <span className="font-bold text-2xl text-slate-900 tracking-tight" style={{ fontFamily: "Manrope" }}>
            HeDesk
          </span>
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Manrope" }}>
            Start your free trial
          </h1>
          <p className="text-sm text-slate-600 mb-6">14 days free. No credit card.</p>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11 mb-4 border-slate-300"
            onClick={handleGoogleSignup}
            data-testid="google-signup-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">or sign up with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="Dr. Anjali Mehta"
                data-testid="signup-name-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="clinic_name">Clinic name</Label>
              <Input
                id="clinic_name"
                name="clinic_name"
                value={form.clinic_name}
                onChange={handleChange}
                required
                placeholder="Sunrise Clinic"
                data-testid="signup-clinic-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="doctor@example.com"
                data-testid="signup-email-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                required
                minLength={6}
                placeholder="At least 6 characters"
                data-testid="signup-password-input"
                className="mt-1.5 h-11"
              />
            </div>

            {error && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                data-testid="signup-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700"
              data-testid="signup-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create clinic account"}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-slate-600 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 font-medium hover:underline" data-testid="signup-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
